<script lang="ts">
    import { _ } from "svelte-i18n"
    import CheckoutFooter from "./CheckoutFooter.svelte"
    import FormInputText from "../../widgets/Form/FormInputText.svelte"
    import FormInputRadio from "../../widgets/Form/FormInputRadio.svelte"
    import { onMount } from "svelte"
    import Select from "svelte-select/Select.svelte"
    import { selectTouchFix } from "../../../lib/actions/selectTouchFix"
    import { getCountriesList, getCountry } from "../../../api"
    import { equal } from "../../../utils"
    import {
        defaultVAT,
        maxInputLength,
        paymentMethods,
        sameBillingAndDeliveryAddress,
    } from "../../../../../api/hooks/config-client"
    import { langField } from "../../../i18n"
    import { spaNavigate } from "../../../functionTools/navigation"
    import { cart, checkoutData, getAsyncCartSum } from "../../../functionTools/cart"
    import { login } from "../../../functionTools/login"
    import { cartItemToInput, trackItemsEvent } from "../../../lib/ga"

    let oldCheckSame = $state($checkoutData.sameAddress)
    let paymentForm = $state()
    const checkSame = () => {
        $checkoutData.sameAddress = equal($checkoutData.deliveryAddress, $checkoutData.billingAddress)
    }

    $effect(() => {
        if (!$cart?.length || $login) spaNavigate("/", { replace: true })
        else if ($checkoutData.stepsValid < 1) spaNavigate("/checkout", { replace: true })
    })

    // Bei kostenlosem Warenkorb Payment-Seite überspringen
    $effect(() => {
        if (cartSum?.totalGross === 0) {
            spaNavigate("/checkout/overview", { replace: true })
        }
    })

    onMount(() => {
        if ($checkoutData.billingAddressInit) {
            delete $checkoutData.billingAddressInit
            oldCheckSame = false
        } else checkSame()
    })

    const invalidate = () => {
        $checkoutData.stepsValid = 1
        checkSame()
    }

    $effect(() => {
        if (!oldCheckSame && $checkoutData.sameAddress) {
            Object.keys($checkoutData.deliveryAddress).forEach((k) => {
                $checkoutData.billingAddress[k as keyof Address] = $checkoutData.deliveryAddress[k as keyof Address]
            })
        }
        oldCheckSame = $checkoutData.sameAddress
    })

    let oldCode: any = $state()
    $effect(() => {
        let code = $checkoutData.billingAddress.countryCode
        if (oldCode != code) {
            getCountry($checkoutData.billingAddress.countryCode).then((c) => {
                $checkoutData.vat = c?.vat ?? defaultVAT
            })
        }
        oldCode = code
    })

    let cartSum: CartSum = $state()

    $effect(() => {
        ;(async () => {
            cartSum = await getAsyncCartSum($cart)
        })()
    })

    $effect(() => {
        if (sameBillingAndDeliveryAddress && !$checkoutData.sameAddress) {
            $checkoutData.sameAddress = true
            Object.keys($checkoutData.deliveryAddress).forEach((k) => {
                $checkoutData.billingAddress[k as keyof Address] = $checkoutData.deliveryAddress[k as keyof Address]
            })
        }
    })

    let country: { label: string; value: string } | null = $state(null)
    let countries: { label: string; value: string }[] = $state([])
    getCountriesList().then((cs) => {
        countries = cs
            .filter((c) => c.valid_for_order)
            .map((c) => ({
                label: langField(c.name, ""),
                value: c.code,
            }))
        country = countries.find((c) => c.value === $checkoutData.billingAddress.countryCode) || null
    })
</script>

<div class="subline">{$_("checkout.order")}</div>
<h2 class="h2_nooffset">{$_("checkout.payment")}</h2>

<form
    bind:this={paymentForm}
    onsubmit={(e) => {
        e.preventDefault()
        checkSame()
        // GA4 add_payment_info (vor dem Weiterleiten)
        try {
            if ($cart?.length) {
                trackItemsEvent("add_payment_info", $cart.map(cartItemToInput), $login ? 0 : ($checkoutData.vat ?? defaultVAT), {
                    payment_type: $checkoutData.payment?.type || "",
                })
            }
        } catch {
            // Tracking darf nichts blockieren
        }
        $checkoutData.stepsValid = 2
        spaNavigate("/checkout/overview")
    }}
>
    <section>
        <div class="section_headline">{$_("checkout.payment-type")}</div>
        <div class="payment-methods">
            {#each paymentMethods as pMethod}
                <label class="payment-method-row" class:selected={$checkoutData.payment.type === pMethod} data-testid="payment-method-row">
                    <input
                        type="radio"
                        name="payment-method"
                        value={pMethod}
                        bind:group={$checkoutData.payment.type}
                        required={true}
                    />
                    <div class="payment-left">
                        <span class="radio-indicator"></span>
                        {#if pMethod === "paypal"}
                            <img src="/media/PaypalGroup-black.svg" alt="PayPal" class="paypal-logo" />
                        {:else}
                            <span class="payment-label">{$_("checkout." + pMethod)}</span>
                        {/if}
                    </div>
                    {#if pMethod === "paypal"}
                        <div class="payment-right">
                            <span class="payment-info-text"
                                >auch ohne Paypal-Account zahlbar mit Kreditkarte oder Lastschrift</span
                            >
                            <div class="payment-logos">
                                <img src="/media/visa.svg" alt="Visa" class="card-logo" />
                                <img src="/media/maestro.svg" alt="Maestro" class="card-logo" />
                                <img src="/media/mastercard.svg" alt="Mastercard" class="card-logo" />
                                <img src="/media/americanexpress.svg" alt="American Express" class="card-logo" />
                                <img src="/media/sepa.svg" alt="SEPA" class="card-logo" />
                            </div>
                        </div>
                    {/if}
                </label>
            {/each}
        </div>
    </section>

    <section>
        <div class="section_headline">{$_("checkout.billing-address")}</div>
        <FormInputRadio
            label={$_("checkout.same-as-shipping-address")}
            bind:group={$checkoutData.sameAddress}
            value={true}
            checked={true}
        />
        <FormInputRadio
            label={$_("checkout.different-billing-address")}
            bind:group={$checkoutData.sameAddress}
            value={false}
        />
        {#if !$checkoutData.sameAddress}
            <div class="row">
                <div class="col-md-6">
                    <FormInputText
                        name="billing_firstname"
                        label="{$_('checkout.firstname')} / {$_('checkout.address-supplement')}"
                        bind:value={$checkoutData.billingAddress.firstname}
                        onChange={invalidate}
                        maxlength={maxInputLength.firstname}
                    />
                </div>
                <div class="col-md-6">
                    <FormInputText
                        name="billing_lastname"
                        label="{$_('checkout.lastname')} / {$_('checkout.company')}"
                        bind:value={$checkoutData.billingAddress.lastname}
                        required
                        onChange={invalidate}
                        maxlength={maxInputLength.lastname}
                    />
                </div>
            </div>
            <div class="row">
                <div class="col-md-10">
                    <FormInputText
                        name="billing_street"
                        label={$_("checkout.street")}
                        bind:value={$checkoutData.billingAddress.street}
                        required
                        onChange={invalidate}
                        maxlength={maxInputLength.street}
                    />
                </div>
                <div class="col-md-2">
                    <FormInputText
                        name="billing_houseNumber"
                        label={$_("checkout.house-number")}
                        bind:value={$checkoutData.billingAddress.houseNumber}
                        required
                        onChange={invalidate}
                        maxlength={maxInputLength.houseNumber}
                    />
                </div>
                <div class="col-md-6">
                    <FormInputText
                        name="billing_postcode"
                        label={$_("checkout.postcode")}
                        bind:value={$checkoutData.billingAddress.postcode}
                        required
                        onChange={invalidate}
                        pattern="^\d{'{5}'}$"
                        maxlength={maxInputLength.postcode}
                    />
                </div>
                <div class="col-md-6">
                    <FormInputText
                        name="billing_city"
                        label={$_("checkout.city")}
                        bind:value={$checkoutData.billingAddress.city}
                        required
                        onChange={invalidate}
                        maxlength={maxInputLength.city}
                    />
                </div>
            </div>
            <div class="row">
                <div class="col-md-12" use:selectTouchFix>
                    <Select
                        items={countries}
                        inputAttributes={{ autocomplete: "off" }}
                        placeholder="Land"
                        showChevron={true}
                        required={true}
                        bind:value={country}
                        on:change={(e) => {
                            if (e.detail.value) {
                                $checkoutData.billingAddress.countryCode = e.detail.value
                            }
                        }}
                    />
                </div>
            </div>
        {/if}
    </section>

    {#if cartSum?.totalGross > 500 && $checkoutData?.payment?.type == "invoice"}
        <section>
            <div>
                {@html $_("checkout.invoice-possible-notice-html")}
            </div>
        </section>
    {/if}
    <CheckoutFooter
        backlink="/checkout"
        backtitle={$_("checkout.back-to-shipping")}
        nexttitle="weiter zur Zusammenfassung"
        submitDisabled={!$checkoutData?.payment?.type ||
            ($checkoutData?.payment?.type == "invoice" && cartSum?.totalGross > 500)}
    />
</form>

<style lang="less">
    @import "../../../css/variables.less";

    .payment-methods {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 1.5rem;
    }

    .payment-method-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 24px;
        border: 1px solid var(--grey-200);
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.2s ease;
        background: var(--grey-100);
        width: 100%;

        @media (max-width: 650px) {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
        }

        &:hover {
            border-color: var(--grey-300);
        }

        &.selected {
            border-color: var(--red-100, #c4102d);
        }

        input[type="radio"] {
            position: absolute;
            opacity: 0;
            pointer-events: none;
        }
    }

    .payment-left {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .radio-indicator {
        width: 24px;
        height: 24px;
        border: 1px solid var(--red-100, #c4102d);
        border-radius: 50%;
        position: relative;
        flex-shrink: 0;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 4px;
        box-sizing: border-box;

        &::after {
            content: "";
            width: 16px;
            height: 16px;
            background: transparent;
            border-radius: 50%;
            transition: background 0.2s ease;
        }
    }

    .payment-method-row.selected .radio-indicator::after {
        background: var(--red-100, #c4102d);
    }

    .paypal-logo {
        height: 33px;
        width: auto;
    }

    .payment-label {
        font-size: 1.5rem;
        font-weight: 400;
        color: var(--grey-500);
    }

    .payment-right {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        padding-left: 24px;
        border-left: 1px solid var(--grey-300);

        @media (max-width: 650px) {
            width: 100%;
            padding-left: 36px;
            border-left: none;
        }
    }

    .payment-info-text {
        font-size: 0.75rem;
        color: var(--grey-500);
    }

    .payment-logos {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        @media (max-width: 650px) {
            justify-content: flex-start;
            gap: 8px;
        }
    }

    .card-logo {
        width: 48px;
        height: auto;
        display: block;

        @media (max-width: 650px) {
            width: 36px;
        }
    }
</style>
